import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { Icon } from '@progress/kendo-react-common';
import {
  DropDownList,
  DropDownListChangeEvent,
  DropDownListFilterChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { FilterDescriptor } from '@progress/kendo-react-dropdowns/dist/npm/common/filterDescriptor';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import CircularIconButton from 'src/components/design-system/CircularIconButton';
import useViewDepartment from 'src/hooks/useViewDepartments';

interface DepartmentDropdownProps {}

const DepartmentDropdown: FC<DepartmentDropdownProps> = () => {
  const { departmentList, selectedDepartment, setSelectedDepartment, confirmBeforeProceeding, t } =
    useViewDepartment();
  const [data, setData] = useState(departmentList);

  useEffect(() => {
    if (departmentList) {
      setData(departmentList);
    }
  }, [departmentList]);

  const filterData = useCallback(
    (filter: FilterDescriptor | CompositeFilterDescriptor) => {
      const data = [...departmentList];
      return filterBy([...data], filter);
    },
    [departmentList]
  );

  const filterChange = useCallback(
    (event: DropDownListFilterChangeEvent) => {
      setData(filterData(event.filter));
    },
    [filterData]
  );

  const handleDepartmentChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const { value } = e.target;
      confirmBeforeProceeding(() => {
        setSelectedDepartment(value);
      });
    },
    [confirmBeforeProceeding, setSelectedDepartment]
  );

  const dropDownIndexByDepartment = useMemo(
    () =>
      departmentList
        ? departmentList.findIndex(
            (department) => department.departmentId === selectedDepartment?.departmentId
          )
        : -1,
    [departmentList, selectedDepartment?.departmentId]
  );

  const isLeftBtnDropDwnDisabled: boolean = useMemo(() => {
    if (dropDownIndexByDepartment - 1 < 0) return true;
    else return false;
  }, [dropDownIndexByDepartment]);

  const isRightBtnDropDwnDisabled: boolean = useMemo(() => {
    if (dropDownIndexByDepartment + 1 >= departmentList.length) return true;
    else return false;
  }, [departmentList.length, dropDownIndexByDepartment]);

  const onLeftBtnDropDwnClick = useCallback(() => {
    confirmBeforeProceeding(() => {
      setSelectedDepartment(departmentList[dropDownIndexByDepartment - 1]);
    });
  }, [confirmBeforeProceeding, departmentList, dropDownIndexByDepartment, setSelectedDepartment]);

  const onRightBtnDropDwnClick = useCallback(() => {
    confirmBeforeProceeding(() => {
      setSelectedDepartment(departmentList[dropDownIndexByDepartment + 1]);
    });
  }, [confirmBeforeProceeding, departmentList, dropDownIndexByDepartment, setSelectedDepartment]);

  const listNoDataRender = useCallback(
    () => (
      <div className="p-4 text-center text-gray-500 text-sm">
        <Icon name="warning" className="text-2xl mb-2" />
        <p>{t('NO DATA FOUND.')}</p>
      </div>
    ),
    [t]
  );

  return (
    <div>
      <div className="flex items-center gap-2 mt-1">
        <span
          id="arrow-end-left-anchor"
          data-tooltip-id="global-tooltip"
          data-tooltip-content={
            isLeftBtnDropDwnDisabled ? 'No Previous Department' : 'Previous Department'
          }
        >
          <CircularIconButton
            icon="arrow-end-left"
            disabled={isLeftBtnDropDwnDisabled}
            onClick={onLeftBtnDropDwnClick}
          />
        </span>
        <DropDownList
          id="view-drop-down-list"
          data={data}
          onChange={handleDepartmentChange}
          value={selectedDepartment}
          textField={t('departmentName')}
          dataItemKey="departmentId"
          style={{ width: '250px' }}
          filterable
          onFilterChange={filterChange}
          listNoDataRender={listNoDataRender}
        />
        <span
          id="arrow-end-right-anchor"
          data-tooltip-id="global-tooltip"
          data-tooltip-content={
            isRightBtnDropDwnDisabled ? 'No Next Department' : 'Next Department'
          }
        >
          <CircularIconButton
            icon="arrow-end-right"
            disabled={isRightBtnDropDwnDisabled}
            onClick={onRightBtnDropDwnClick}
          />
        </span>
      </div>
    </div>
  );
};

export default DepartmentDropdown;
